import { Dispatch, FC, SetStateAction, useEffect, useRef } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { selectedStudentSkillStates } from '../../redux/reducers/studentSkillsSlice';
import { PreloadedQuery } from 'react-relay';
import { DashboardGetStudentExitSlipQuery } from '../../graphQLOperations/__generated__/DashboardGetStudentExitSlipQuery.graphql';
import DashboardExitSlipQuery from './DashboardExitSlipQuery';
import { FoundationSkills } from './DashboardContainerBody';
import { PathSkillNode } from '../../businessLogicService/StudentDashboardAux/pathToSkills';
import { useDashboardUnitPath } from '../../hooks/useDashboardUnitPath';
import { scrollToUnitPathPosition } from '../../custom-hooks/useUnitPathPositionAnchor';
import UnitPathRail from './UnitPathRail';
import { DashboardCompletedBanner } from './DashboardCompletedBanner';
import { DashboardPracticeProblemsActionsQuery$data } from '../../graphQLOperations/__generated__/DashboardPracticeProblemsActionsQuery.graphql';

interface Props {
  foundationsSkills: FoundationSkills[] | null;
  pathsToSkills?: PathSkillNode[][][];
  queryReferenceExitSlip?: PreloadedQuery<DashboardGetStudentExitSlipQuery, Record<string, unknown>>;
  setSkillIdForRefresherCard: Dispatch<SetStateAction<string | null>>;
  lessonPractice: DashboardPracticeProblemsActionsQuery$data['learnosityPracticeProblemsSessions'];
}

const DashboardCards: FC<Props> = ({
  queryReferenceExitSlip,
  setSkillIdForRefresherCard,
  pathsToSkills,
  foundationsSkills,
  lessonPractice,
}) => {
  const studentSkills = useAppSelector(selectedStudentSkillStates);
  const { unitPath, positionKey, hasPassedConnector } = useDashboardUnitPath({
    paths: pathsToSkills,
    foundationSkills: foundationsSkills ?? [],
    lessonPractice,
  });

  const hasAutoScrolledRef = useRef(false);

  // Scroll the student to their current position once, the moment it becomes available, so they land
  // where they left off instead of at the top of a possibly long unit path.
  useEffect(() => {
    if (hasAutoScrolledRef.current || !hasPassedConnector || positionKey === null) {
      return;
    }
    hasAutoScrolledRef.current = true;
    setTimeout(() => {
      scrollToUnitPathPosition(hasPassedConnector);
    }, 200);
  }, [hasPassedConnector, positionKey]);

  return (
    <>
      {studentSkills && (
        <div className="paper-grid-bg-skills tw-relative">
          <div className=" tw-w-full dashboard-card !tw-p-0 tw-h-full tw-flex tw-flex-col tw-items-center">
            <div className="unit-path-scale tw-w-full">{unitPath}</div>
            <DashboardCompletedBanner />
          </div>
          <div className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-1 tw-z-20 tw-w-fit">
            <UnitPathRail positionKey={positionKey} hasPassedConnector={hasPassedConnector} />
          </div>
        </div>
      )}
      {queryReferenceExitSlip && (
        <DashboardExitSlipQuery
          queryReferenceExitSlip={queryReferenceExitSlip}
          setSkillIdForRefresherCard={setSkillIdForRefresherCard}
        />
      )}
    </>
  );
};

export default DashboardCards;
